import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { API } from "@/lib/api";
import { Printer, FilePdf, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const VERIFY_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PrintLabels() {
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState("");
  const [items, setItems] = useState([]);
  const [params] = useSearchParams();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/qr/batches");
      setBatches(data);
      const q = params.get("batch");
      const chosen = q || data?.[0]?.batch_id || "";
      setActiveBatch(chosen);
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!activeBatch) return;
    (async () => {
      const { data } = await api.get("/admin/qr/list", { params: { batch_id: activeBatch, limit: 500 } });
      setItems(data.items);
    })();
  }, [activeBatch]);

  const downloadPdf = async () => {
    try {
      const t = localStorage.getItem("tvs_token");
      const res = await fetch(`${API}/admin/qr/print-pdf?batch_id=${activeBatch}`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-labels-${activeBatch}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  return (
    <div className="space-y-6" data-testid="print-root">
      <div className="flex items-end justify-between flex-wrap gap-4 print:hide">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Label Studio</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Print QR labels</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            A4 layout, 40 labels per page (4×10 grid). Ready for industrial label printers.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={activeBatch} onValueChange={setActiveBatch}>
            <SelectTrigger className="w-72 h-11" data-testid="batch-selector">
              <SelectValue placeholder="Choose a batch" />
            </SelectTrigger>
            <SelectContent>
              {batches.map((b) => (
                <SelectItem key={b.batch_id} value={b.batch_id} data-testid={`batch-option-${b.batch_id}`}>
                  {b.part_number} · {b.batch_number} · ({b.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => window.print()} className="bg-[#253c80] hover:bg-[#1a2a5e] text-white h-11" data-testid="print-button">
            <Printer size={18} weight="bold" className="mr-1.5" /> Print
          </Button>
          <Button onClick={downloadPdf} variant="outline" className="h-11" data-testid="download-pdf-button">
            <FilePdf size={18} weight="bold" className="mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>

      {!activeBatch && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
          <ArrowsCounterClockwise size={28} className="mx-auto mb-3 text-slate-400" />
          No batches yet — head to <span className="font-semibold">Generate QR</span> to create your first one.
        </div>
      )}

      {/* Print sheet */}
      {activeBatch && (
        <div className="bg-white rounded-lg border border-slate-200 print:border-0 print:rounded-none">
          <div className="px-6 py-3 border-b border-slate-200 text-xs text-slate-500 print:hide">
            Showing {items.length} labels · Batch <span className="font-mono font-semibold text-slate-700">{activeBatch}</span>
          </div>
          <div className="p-6 print:p-0">
            <div className="grid grid-cols-4 gap-2 print:gap-0">
              {items.map((it) => (
                <div
                  key={it.unique_code}
                  className="border border-slate-200 rounded-md p-2 flex flex-col items-center text-center bg-white print:break-inside-avoid"
                  data-testid={`label-${it.unique_code}`}
                >
                  <QRCodeCanvas
                    value={`${VERIFY_BASE}/verify/${it.unique_code}`}
                    size={96}
                    fgColor="#0f172a"
                    bgColor="#ffffff"
                    level="M"
                    includeMargin={false}
                  />
                  <div className="mt-1.5 w-full">
                    <div className="text-[10px] font-bold text-slate-900 truncate">PN: {it.part_number}</div>
                    <div className="text-[9px] text-slate-600 truncate">{it.part_name}</div>
                    <div className="text-[9px] font-mono text-[#253c80] mt-0.5">{it.unique_code}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
